import moment from 'moment'

import { roleForNewInterval } from '../../../../shared/priority-bands'
import { rangeHoldsRun, sameIntervalId, screenStandingAmong, NO_STANDING } from '../calendar-intervals'
import { NEW_INTERVAL_MINUTES, takeoverWindow, defaultNewStart } from '../new-interval-times'
import { combineDateAndTime, formatRange } from 'common/date-time'
import { endDayOf, hasUnsavedEdits, withEdit, withSavedTimes } from '../panel-draft'
import { PanelSave } from './panel-save'

/** The question before the panel drops edits that the operator did not save. */
const DISCARD_QUESTION = 'Discard your changes to this interval?'

/** Join a date field and a time field of the panel into a Date, or null. */
function dateOf (datePart, timePart) {
  const joined = combineDateAndTime(datePart, timePart)

  return joined ? joined.toDate() : null
}

export class StateManager {
  constructor (controller, SceneCollectionService) {
    this.controller = controller
    this.SceneCollectionService = SceneCollectionService

    this.availableSceneCollections = []

    // The state of the inspector panel, or null while the panel is closed:
    // { baseline, draft, endDay, standing, saving }. Each opening gets a new
    // object, so the panel can tell an opening from an edit. Create and edit
    // use the same shape, because the server has one write path.
    this.panel = null

    this.panelSave = new PanelSave(this, SceneCollectionService)
  }

  /** The draft that the panel shows, or null. The other managers read its id. */
  get inspecting () {
    return this.panel ? this.panel.draft : null
  }

  /**
     * Load the available scene collections for the current scene.
     *
     * @returns {Promise} The scene collections
     */
  loadAvailableSceneCollections () {
    if (!this.controller.scene || !this.controller.scene.id) {
      this.controller._$log.error('[ScheduleCalendar] No scene ID available for loading collections')
      return Promise.reject(new Error('Scene ID required'))
    }

    return this.SceneCollectionService.getSceneCollectionsForScene(this.controller.scene.id)
      .then(sceneCollections => {
        this.availableSceneCollections = sceneCollections || []
        this.controller._$log.info('[ScheduleCalendar] Loaded scene collections:', this.availableSceneCollections.length)
        return this.availableSceneCollections
      })
      .catch(error => {
        this.controller._$log.error('[ScheduleCalendar] Failed to load scene collections:', error)
        this.availableSceneCollections = []
        throw error
      })
  }

  /** True when the draft of the panel has edits that are not saved. */
  hasUnsavedEdits () {
    return Boolean(this.panel) && hasUnsavedEdits(this.panel.baseline, this.panel.draft)
  }

  /**
   * Tell whether the panel can show something else. Edits that are not saved
   * get a question first, so a click elsewhere does not lose them silently.
   *
   * Every operator action that replaces or closes the panel calls this. A
   * save, a delete and a drag of the bar do not, because nothing is lost.
   *
   * @returns {boolean} False when the operator keeps the edits
   */
  mayReplacePanel () {
    if (!this.hasUnsavedEdits()) {
      return true
    }

    return window.confirm(DISCARD_QUESTION)
  }

  /**
     * Open or close the inspector panel. Every path that opens or closes the
     * panel calls this method.
     *
     * The panel takes width from the grid. The resize observer of the calendar
     * manager sees that change, so this method does not report it.
     *
     * @param {Object|null} baseline - The stored row, or the values that a new
     *   interval opens with. Null closes the panel
     * @param {Object} [draft] - The values that the panel shows. The default is the baseline
     */
  setInspecting (baseline, draft = baseline) {
    this.panel = baseline ? this.openedPanel(baseline, draft) : null

    this.dropStaleTimeSelection()
    this.moveOutline()
  }

  /** The panel state of a new opening. */
  openedPanel (baseline, draft) {
    const opened = Object.freeze(Object.assign({}, draft))
    const before = Object.assign({}, NO_STANDING, { role: baseline.role || null })

    return {
      baseline: Object.freeze(Object.assign({}, baseline)),
      draft: opened,
      endDay: endDayOf(opened),
      standing: this.standingOf(opened, before),
      // True while a save of this opening waits for the server. The panel is
      // then read-only, so a second click cannot send a second write.
      saving: false
    }
  }

  /** The outline moves on every change of the interval, and not only when the panel opens or closes. */
  moveOutline () {
    this.controller._$timeout(() => {
      if (this.controller.calendarManager) {
        this.controller.calendarManager.markInspected()
      }
    }, 0)
  }

  /**
   * The panel sends each edit of the operator here. The draft and its
   * standing then change together.
   *
   * @param {Object} changes - The changed fields and their new values
   */
  editPanel (changes) {
    if (!this.panel) {
      return
    }

    this.takeDraft(withEdit(this.panel, changes))
  }

  takeDraft ({ draft, endDay }) {
    Object.assign(this.panel, { draft, endDay })
    this.updateStanding()
  }

  /**
   * The standing describes the values that a save sends. Each change of the
   * draft and each change of the calendar events calls this.
   */
  updateStanding () {
    this.panel.standing = this.standingOf(this.panel.draft, this.panel.standing)
  }

  /**
   * A drag on empty grid leaves its block on the grid, and the panel then
   * describes that block. The block goes as soon as the panel holds something
   * else, or nothing. A stored interval has an id, and an unsaved new one does
   * not.
   */
  dropStaleTimeSelection () {
    if (this.controller.calendarManager && (!this.inspecting || this.inspecting.intervalId)) {
      this.controller.calendarManager.clearTimeSelection()
    }
  }

  /**
     * Open the inspector on an existing interval.
     */
  inspect (calendarEvent) {
    if (!this.mayReplacePanel()) {
      return
    }

    this.showInPanel(calendarEvent)
  }

  /** Open the inspector on an existing interval. The caller asked about edits already. */
  showInPanel (calendarEvent) {
    const snapshot = this.snapshotOf(calendarEvent)

    if (!snapshot) {
      this.controller._$log.warn('[ScheduleCalendar] No interval data on the event')
      return
    }

    this.loadAvailableSceneCollections()
    this.setInspecting(snapshot)
  }

  /**
   * A drag or a resize saves the times without the panel. The saved row is
   * the new baseline, so the next save of the panel does not write the old
   * times. The draft takes the times and keeps the other edits of the operator.
   */
  followSavedTimes (calendarEvent, updatedAt) {
    const snapshot = this.snapshotOf(calendarEvent)

    if (!snapshot || !this.panel || !sameIntervalId(this.panel.draft.intervalId, snapshot.intervalId)) {
      return
    }

    // Without a new value the old one stays, and the server then refuses a
    // panel save instead of skipping its check.
    const row = Object.freeze(Object.assign(snapshot, { updatedAt: updatedAt || snapshot.updatedAt }))

    this.panel.baseline = row
    this.takeDraft(withSavedTimes(this.panel.draft, row))
    this.moveOutline()
  }

  /**
     * The band peers of an interval, and how much of it a save puts on screen.
     * Also whether a raise above those peers puts more of it on screen.
     *
     * Times outside the range on show give NO_STANDING. The calendar then
     * does not hold every overlap.
     *
     * @param {Object} edited - The values that a save sends
     * @returns {Object|null} { peers, under, over, screen, refusal,
     *   raiseGainsScreen, known }. Null when the calendar does not hold the row
     */
  screenStandingOf (edited) {
    const week = this.controller.calendarManager.week()
    const candidate = {
      id: edited.intervalId,
      role: edited.role,
      start: dateOf(edited.startDate, edited.startTime),
      end: dateOf(edited.endDate, edited.endTime)
    }

    return rangeHoldsRun(week.range, candidate) ? screenStandingAmong(week.intervals, candidate) : NO_STANDING
  }

  /**
     * The standing of a draft, with the role that the panel shows and that a
     * save sends.
     *
     * A null answer keeps the last standing. Then the calendar does not hold
     * the row, for example after a delete from another page.
     *
     * @param {Object} draft - The draft of the panel
     * @param {Object} previous - The last standing, with its role
     * @returns {Object} The screen standing and { role }
     */
  standingOf (draft, previous) {
    const role = this.roleShown(draft, previous.role)
    const standing = this.screenStandingOf(Object.assign({}, draft, { role }))

    return Object.freeze(Object.assign({}, standing || previous, { role }))
  }

  /**
     * The role of a draft. A new interval without a selected role takes the
     * role that roleForNew gives for its times. Times with no run have no
     * overlaps to derive a role from, so the last role stays. A stored
     * interval never changes its role without a click.
     */
  roleShown (draft, previousRole) {
    if (draft.role || draft.intervalId) {
      return draft.role || null
    }

    const start = dateOf(draft.startDate, draft.startTime)
    const end = dateOf(draft.endDate, draft.endTime)

    return start && end && end > start ? this.roleForNew(start, end) : previousRole || null
  }

  /**
     * The calendar changed its events. A fetch, a drag of another bar and a
     * delete all change them, so the standing of the panel changes too.
     */
  followCalendarEvents () {
    if (this.panel) {
      this.updateStanding()
    }
  }

  snapshotOf (calendarEvent) {
    const extendedProps = calendarEvent && calendarEvent.extendedProps

    if (!extendedProps || !extendedProps.intervalId) {
      return null
    }

    const start = moment(calendarEvent.start)
    const end = moment(calendarEvent.end)

    return {
      intervalId: extendedProps.intervalId,
      sceneCollectionId: extendedProps.sceneCollectionId,
      role: extendedProps.role || null,
      startDate: start.toDate(),
      startTime: start.toDate(),
      endDate: end.toDate(),
      endTime: end.toDate(),
      updatedAt: extendedProps.updatedAt || null
    }
  }

  /**
     * Open the inspector on a new interval.
     *
     * A drag on an empty background gives the times. The toolbar button gives a
     * default hour in the range that the grid shows.
     *
     * @param {Date} [startTime]
     * @param {Date} [endTime]
     * @param {string} [role] - Also fill in the role. Without it, roleForNew
     *   derives the role from the overlaps on show
     */
  inspectNew (startTime, endTime, role) {
    if (!this.mayReplacePanel()) {
      // The new drag left a block that the panel does not describe. The grid
      // holds one block only, so the block of the panel cannot come back.
      if (this.controller.calendarManager) {
        this.controller.calendarManager.clearTimeSelection()
      }
      return
    }

    const calendarManager = this.controller.calendarManager
    const start = startTime
      ? moment(startTime)
      : defaultNewStart(moment(), calendarManager ? calendarManager.visibleRange() : null)
    const end = endTime ? moment(endTime) : moment(start).add(NEW_INTERVAL_MINUTES, 'minutes')

    this.loadAvailableSceneCollections()

    // The draft has a role only when the caller names one. Otherwise the code
    // selects the role in the same way as the server derives it, so the usual
    // case needs no click. The role stays visible and editable. A derived role
    // follows the typed times until the operator selects one.
    const draft = {
      intervalId: null,
      sceneCollectionId: null,
      role: role || null,
      startDate: start.toDate(),
      startTime: start.toDate(),
      endDate: end.toDate(),
      endTime: end.toDate()
    }

    this.setInspecting(Object.assign({}, draft, { role: role || this.roleForNew(start.toDate(), end.toDate()) }), draft)
  }

  /**
     * Start a takeover over the interval that the panel shows.
     *
     * The panel changes to the new interval. The operator then selects a
     * playlist and saves. The code writes nothing before the save, and it does
     * not change the interval below.
     *
     * The takeover uses the times of the draft, which the operator can see,
     * and not the times of the last write.
     */
  punchInTakeover () {
    const current = this.inspecting

    if (!current || !current.intervalId) {
      return
    }

    const opening = takeoverWindow(
      combineDateAndTime(current.startDate, current.startTime),
      combineDateAndTime(current.endDate, current.endTime))

    if (!opening) {
      this.controller._$log.warn('[ScheduleCalendar] Cannot read the times to punch a takeover into')
      return
    }

    this.inspectNew(opening.start, opening.end, 'takeover')
  }

  /**
     * The most probable role for a new interval, or null when it is unknown.
     *
     * The rule is roleForNewInterval, which the server also uses. The calendar
     * events on show are the overlaps that the panel can know. One overlap on
     * show is enough for the answer. When none is on show and the times leave
     * the range on show, the answer is unknown. A save then sends no role, and
     * the server derives the role from all the overlaps.
     */
  roleForNew (start, end) {
    if (!this.controller.calendarManager) {
      return null
    }

    const week = this.controller.calendarManager.week()

    if (week.intervals.some(interval => start < interval.end && end > interval.start)) {
      return roleForNewInterval(true)
    }

    return rangeHoldsRun(week.range, { start, end }) ? roleForNewInterval(false) : null
  }

  /** The operator closes the panel. */
  closeInspector () {
    if (!this.mayReplacePanel()) {
      return
    }

    this.clearInspector()
  }

  /**
   * Close the panel without a question. A save or a delete has written the
   * edits, and a page that closes cannot keep them.
   */
  clearInspector () {
    this.setInspecting(null)
  }

  /**
   * The question before a delete. It names the playlist and the stored times.
   *
   * The grid menu reaches a narrow bar beside another one, so the operator
   * needs to see which interval goes. The calendar can lack the row, for
   * example after a move to another week. The question then names nothing.
   */
  deleteQuestion (target) {
    const shown = this.controller.calendarManager.week().intervals
      .find(interval => sameIntervalId(interval.id, target.intervalId))

    if (!shown) {
      return 'Delete this interval? This cannot be undone.'
    }

    return `Delete ${shown.title}, ${formatRange(shown.start, shown.end)}? This cannot be undone.`
  }

  deleteInterval (interval) {
    const target = interval || this.inspecting

    if (!target || !target.intervalId) {
      return
    }

    if (!window.confirm(this.deleteQuestion(target))) {
      return
    }

    this.controller._$log.info('[ScheduleCalendar] Deleting interval:', target.intervalId)

    return this.SceneCollectionService.deleteInterval(target.intervalId)
      .then(() => {
        this.controller._$log.info('[ScheduleCalendar] Interval deleted successfully')

        // The grid menu can delete a bar while the panel shows another interval.
        if (this.inspecting && sameIntervalId(this.inspecting.intervalId, target.intervalId)) {
          this.clearInspector()
        }
      })
      .catch(error => {
        this.controller._$log.error('[ScheduleCalendar] Error deleting interval:', error)
        window.alert('Could not delete the interval. Check the connection and try again.')
      })
  }
}
